/**
 * Authentication Hook using AWS Amplify
 *
 * Provides authentication state and methods using AWS Cognito via Amplify.
 * Uses OAuth2 authorization code flow with Cognito Hosted UI.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  signInWithRedirect,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  AuthUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import * as Sentry from "@sentry/react";
import { api } from '../services/api';
import { isCognitoConfigured, isOAuthConfigured } from '../lib/amplify';
import { authLogger } from '../lib/logger';
import { toast } from '../lib/toast';

// =============================================================================
// Security Guard - CRITICAL
// =============================================================================

/**
 * Validate that authentication is properly configured for production.
 * This MUST be called before allowing any auth operations.
 */
function validateProductionAuth(): void {
  if (!import.meta.env.DEV && !isCognitoConfigured()) {
    console.error(
      '[SECURITY ERROR] Cognito not configured in production build. ' +
      'Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID environment variables.'
    );
    throw new Error(
      'SECURITY ERROR: Authentication not configured. ' +
      'Contact your administrator.'
    );
  }
}

// =============================================================================
// Types
// =============================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  groups: string[];
}

export type UserRole = 'admin' | 'tester';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isTester: boolean;
  userRole: 'admin' | 'tester' | 'user' | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Parse claims from an ID token for UI display only.
 *
 * SECURITY NOTE: This is client-side extraction without signature verification.
 * Authorization is enforced server-side by API Gateway Cognito Authorizer
 * and Lambda role checks. Never trust these claims for security decisions.
 */
function parseIdTokenClaims(idToken: string): Record<string, unknown> {
  try {
    return JSON.parse(atob(idToken.split('.')[1]));
  } catch {
    return {};
  }
}

/**
 * Convert Amplify AuthUser to our User type.
 *
 * Extracts attributes from the ID token instead of calling fetchUserAttributes(),
 * which requires the aws.cognito.signin.user.admin scope not available on this app client.
 */
async function amplifyUserToUser(authUser: AuthUser): Promise<User> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString() || '';
  const claims = parseIdTokenClaims(idToken);

  const email = (claims.email as string) || '';
  const name = (claims.name as string) || email || 'User';
  const groups = (claims['cognito:groups'] as string[]) || [];

  return {
    id: authUser.userId,
    email,
    name,
    groups,
  };
}

// =============================================================================
// Provider
// =============================================================================

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isForceLoggingOut = useRef(false);

  // SECURITY: Validate auth configuration on mount
  useEffect(() => {
    try {
      validateProductionAuth();
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }, []);

  // Handle 401 responses from the API client.
  const handleUnauthorized = useCallback(() => {
    if (isForceLoggingOut.current) return;
    isForceLoggingOut.current = true;

    authLogger.warn('API returned 401 — verifying session');

    (async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: false });

        if (session.tokens?.idToken) {
          authLogger.warn('401 received but session is still valid — likely an API configuration issue');
          toast.error('Request denied', 'The server rejected the request. If this persists, contact your administrator.');
        } else {
          authLogger.warn('No valid tokens in session — clearing auth state');
          toast.warning('Session expired', 'Your session is no longer valid. Please sign in again.');
          setUser(null);
        }
      } catch {
        authLogger.error('Session verification failed');
        toast.warning('Session expired', 'Your session is no longer valid. Please sign in again.');
        setUser(null);
      } finally {
        setTimeout(() => { isForceLoggingOut.current = false; }, 5000);
      }
    })();
  }, []);

  // Wire up API service with token provider
  useEffect(() => {
    api.setTokenProvider(async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const token = session.tokens?.idToken?.toString() || null;
        if (!token) {
          authLogger.warn('No ID token in session — API requests will be unauthenticated');
        }
        return token;
      } catch (error) {
        authLogger.error('fetchAuthSession failed in token provider:', error);
        return null;
      }
    });
    api.setOnUnauthorized(handleUnauthorized);
  }, [handleUnauthorized]);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Listen for Amplify auth events
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      authLogger.log('Hub event:', payload.event);

      switch (payload.event) {
        case 'signedIn':
          Sentry.metrics.count("auth.event", 1, { attributes: { event: "signed_in" } });
          await checkAuthStatus();
          break;
        case 'signedOut':
          Sentry.metrics.count("auth.event", 1, { attributes: { event: "signed_out" } });
          setUser(null);
          break;
        case 'tokenRefresh':
          Sentry.metrics.count("auth.event", 1, { attributes: { event: "token_refresh" } });
          authLogger.log('Token refreshed — re-evaluating group membership');
          await checkAuthStatus();
          break;
        case 'tokenRefresh_failure':
          Sentry.metrics.count("auth.event", 1, { attributes: { event: "token_refresh_failure" } });
          authLogger.error('Token refresh failed');
          toast.warning('Session expired', 'Please sign in again.');
          setUser(null);
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  /**
   * Check current authentication status
   */
  const checkAuthStatus = async () => {
    setIsLoading(true);

    try {
      const authUser = await getCurrentUser();
      const appUser = await amplifyUserToUser(authUser);

      setUser(appUser);
      authLogger.log('User authenticated');
    } catch {
      authLogger.log('No authenticated user');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Sign in — redirects to Cognito Hosted UI
   */
  const login = useCallback(async () => {
    if (!isOAuthConfigured()) {
      throw new Error(
        'VITE_COGNITO_DOMAIN not set — cannot redirect to Cognito Hosted UI'
      );
    }
    try {
      authLogger.log('Redirecting to Cognito Hosted UI...');
      await signInWithRedirect();
    } catch (error) {
      if (error instanceof Error && error.name === 'UserAlreadyAuthenticatedException') {
        authLogger.warn('Stale session detected — signing out and retrying');
        await signOut();
        await signInWithRedirect();
      } else {
        throw error;
      }
    }
  }, []);

  /**
   * Sign out
   */
  const logout = useCallback(async () => {
    authLogger.log('Signing out...');
    setUser(null);
    try {
      await signOut({ global: true });
    } catch (error) {
      authLogger.error('Sign out failed:', error);
      toast.warning('Sign out incomplete', 'Local session was cleared. If this repeats, refresh and try again.');
    }
  }, []);

  /**
   * Get current ID token (used for API authorization — contains cognito:groups claim)
   */
  const getIdToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const token = session.tokens?.idToken?.toString() || null;
      if (!token) {
        authLogger.warn('No ID token in session');
      }
      return token;
    } catch (error) {
      authLogger.error('fetchAuthSession failed in getIdToken:', error);
      return null;
    }
  }, []);

  // =============================================================================
  // Computed Properties
  // =============================================================================

  const isAdmin = user?.groups.some((g) => g.toLowerCase() === 'admins') ?? false;

  // Any authenticated non-admin user is a tester — no Cognito group required
  const isTester = !!user && !isAdmin;

  const userRole: 'admin' | 'tester' | 'user' | null = !user
    ? null
    : isAdmin
    ? 'admin'
    : 'tester';

  // =============================================================================
  // Context Value
  // =============================================================================

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isAdmin,
    isTester,
    userRole,
    login,
    logout,
    getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
